// hooks/useFunnelNavigation.js - Updated with funnelId option
import { useNavigate, useParams } from 'react-router-dom';
import { useTracking, TrackingEvents } from '../tracking/service';
import { getBrand } from '../utils/getBrand';

const useFunnelNavigation = (defaultFunnelId = '1') => {
  const navigate = useNavigate();
  const { funnelId: paramFunnelId } = useParams();
  const { track } = useTracking();
  // Use funnel ID from params, then the brand's own funnel (e.g. RemoteApply's
  // homepage sends visitors to funnel 35), then the generic default.
  const currentFunnelId = paramFunnelId || getBrand().brandDefaultFunnelNr || defaultFunnelId;

  const navigateTo = (page, params = {}, queryParams = {}, targetFunnelId = null) => {
    // Use specified funnel ID or fall back to current funnel ID
    const effectiveFunnelId = targetFunnelId || currentFunnelId;

    let path = `/${effectiveFunnelId}/${page}`;

    // Add URL parameters if provided
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        path += `/${value}`;
      }
    });

    // Add query parameters if provided
    const urlSearchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      urlSearchParams.set(key, value);
    });

    const queryString = urlSearchParams.toString();
    if (queryString) {
      path += `?${queryString}`;
    }

    navigate(path);
  };

  // Helper to extract options and targetFunnelId from options object
  const extractOptions = (options = {}) => {
    const { funnelId: targetFunnelId, queryParams = {}, code } = options;
    return { targetFunnelId, queryParams, code };
  };

  const navigateToQuiz = (questionNr = 1, options = {}) => {
    const { targetFunnelId, queryParams, code } = extractOptions(options);

    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Quiz',
      funnel_path: `/quiz/${questionNr}`,
      funnel_location: window.location.origin + `/${currentFunnelId}/quiz/${questionNr}`,
      funnel_id: currentFunnelId,
    });

    if (questionNr === 1) {
      track(TrackingEvents.QUIZ_STARTED, {
        funnel_step: 'Quiz',
        funnel_path: `/quiz/${questionNr}`,
        funnel_location: window.location.origin + `/${currentFunnelId}/quiz/${questionNr}`,
        funnel_id: currentFunnelId,
      });
    }

    navigateTo('quiz', { questionNr, code }, queryParams, targetFunnelId);
  };

  const navigateToEmail = (options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Email',
      funnel_path: '/email',
      funnel_location: window.location.origin + `/${currentFunnelId}/email`,
      funnel_id: currentFunnelId,
    });
    track(TrackingEvents.QUIZ_COMPLETED, {
      funnel_step: 'Email',
      funnel_path: '/email',
      funnel_location: window.location.origin + `/${currentFunnelId}/email`,
      funnel_id: currentFunnelId,
    });
    navigateTo('email', {}, queryParams, targetFunnelId);
  };

  const navigateToSummary = (code, email, options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Summary',
      funnel_path: '/summary',
      funnel_location: window.location.origin + `/${currentFunnelId}/summary`,
      funnel_id: currentFunnelId,
    });

    track(TrackingEvents.LEAD_CREATED, {
      funnel_step: 'Summary',
      funnel_path: '/summary',
      funnel_location: window.location.origin + `/${currentFunnelId}/summary`,
      funnel_id: currentFunnelId,
      lead_code: code,
      email: email,
    });
    navigateTo('summary', { code }, queryParams, targetFunnelId);
  };

  const navigateToCheckout = (code, options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    const params = code ? { code } : {};
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Checkout',
      funnel_path: '/checkout',
      funnel_location: window.location.origin + `/${currentFunnelId}/checkout`,
      funnel_id: currentFunnelId,
    });

    track(TrackingEvents.CHECKOUT_STARTED, {
      funnel_step: 'Checkout',
      funnel_path: '/checkout',
      funnel_location: window.location.origin + `/${currentFunnelId}/checkout`,
      funnel_id: currentFunnelId,
      lead_code: code ? code : null,
    });

    navigateTo('checkout', params, queryParams, targetFunnelId);
  };

  const navigateToPayment = (code, options = {}, trackingParams = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    const params = code ? { code } : {};
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Payment',
      funnel_path: '/payment',
      funnel_location: window.location.origin + `/${currentFunnelId}/payment`,
      funnel_id: currentFunnelId,
    });
    track(TrackingEvents.PAYMENT_INFO_ADDED, {
      funnel_step: 'Payment',
      funnel_path: '/payment',
      funnel_location: window.location.origin + `/${currentFunnelId}/payment`,
      funnel_id: currentFunnelId,
      lead_code: code,
      ...trackingParams,
    });

    navigateTo('payment', params, queryParams, targetFunnelId);
  };

  const navigateToUpsell = (code, page, options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);

    // If page is not set, redirect to success
    if (!page) {
      track(TrackingEvents.PAGE_VIEW, {
        funnel_step: 'Success',
        funnel_path: '/success',
        funnel_location: window.location.origin + `/${currentFunnelId}/success`,
        funnel_id: currentFunnelId,
      });
      navigateTo('success', { code }, queryParams, targetFunnelId);
      return;
    }

    // Otherwise, go to upsell
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: page,
      funnel_path: `/${page}`,
      funnel_location: window.location.origin + `/${currentFunnelId}/${page}`,
      funnel_id: currentFunnelId,
    });
    navigateTo(`offer/${page}`, { code }, queryParams, targetFunnelId);
  };

  const navigateToSuccess = (code, options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'Success',
      funnel_path: '/success',
      funnel_location: window.location.origin + `/${currentFunnelId}/success`,
      funnel_id: currentFunnelId,
    });
    navigateTo('success', { code }, queryParams, targetFunnelId);
  };

  const navigateToDeliveryUpsell = (code, options = {}) => {
    const { targetFunnelId, queryParams } = extractOptions(options);
    track(TrackingEvents.PAGE_VIEW, {
      funnel_step: 'special-deal-delivery',
      funnel_path: '/special-deal-delivery',
      funnel_location: window.location.origin + `/${currentFunnelId}/special-deal-delivery`,
      funnel_id: currentFunnelId,
    });
    navigateTo('special-deal-delivery', { code }, queryParams, targetFunnelId);
  };

  const navigateToPagePart = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToNotFound = () => {
    navigate('/404', { replace: true });
  };

  const navigateToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const navigateToOnboarding = () => {
    navigate('/onboarding', { replace: true });
  };

  const navigateToHome = () => {
    navigate('/', { replace: true });
  };

  // Helper method to navigate to a specific funnel explicitly
  const navigateToFunnel = (targetFunnelId, page = 'landing', params = {}, queryParams = {}) => {
    navigateTo(page, params, queryParams, targetFunnelId);
  };

  return {
    funnelId: currentFunnelId,
    navigateTo,
    navigateToQuiz,
    navigateToEmail,
    navigateToSummary,
    navigateToCheckout,
    navigateToPayment,
    navigateToSuccess,
    navigateToNotFound,
    navigateToPagePart,
    navigateToDashboard,
    navigateToOnboarding,
    navigateToFunnel,
    navigateToHome,
    navigateToUpsell,
    navigateToDeliveryUpsell,
  };
};

export default useFunnelNavigation;
